import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  memo,
} from "react";
import { useTranslation } from "react-i18next";
import ServiceCard from "./ServiceCard";
import SliderControls from "./SliderControls";
import { useServicesSliderMetrics } from "../../hooks/useServicesSliderMetrics";

const ServicesSlider = memo(function ServicesSlider({
  services,
  selectedOptions,
  onReset,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filteredServices, setFilteredServices] = useState(services);
  const [selectedTags, setSelectedTags] = useState([]);
  const [reduceMotion, setReduceMotion] = useState(false);

  const listRef = useRef(null);
  const trackRef = useRef(null);
  const pointerStartRef = useRef(null);
  /** Suppress the following click on <a> slides after a horizontal swipe changed the slide. */
  const swipeConsumedRef = useRef(false);

  const slidesKey = useMemo(
    () => filteredServices.map((s) => s.id).join(","),
    [filteredServices]
  );

  const { step: slideStep, padLeft } = useServicesSliderMetrics(
    listRef,
    trackRef,
    slidesKey,
    isRtl
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (selectedOptions) {
      const tags = Object.values(selectedOptions).filter(Boolean);
      setSelectedTags(tags);

      const filtered = services
        .map((service) => {
          const score = tags.reduce(
            (acc, tag) => acc + (service.tags.includes(tag) ? 1 : 0),
            0
          );
          return { ...service, isRecommended: score > 0, score };
        })
        .filter((service) => service.score > 0)
        .sort((a, b) => b.score - a.score);

      setFilteredServices(filtered.length > 0 ? filtered : services);
      setCurrentIndex(0);
    } else {
      setFilteredServices(services);
      setSelectedTags([]);
    }
  }, [selectedOptions, services]);

  useEffect(() => {
    setCurrentIndex((i) =>
      Math.min(i, Math.max(0, filteredServices.length - 1))
    );
  }, [filteredServices.length]);

  const handlePrevClick = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
  }, []);

  const handleNextClick = useCallback(() => {
    setCurrentIndex((prev) =>
      prev < filteredServices.length - 1 ? prev + 1 : prev
    );
  }, [filteredServices.length]);

  const handleResetFilter = useCallback(() => {
    setFilteredServices(services);
    setSelectedTags([]);
    setCurrentIndex(0);
    onReset?.();
  }, [onReset, services]);

  /* LTR: shift left as index grows. RTL: flex packs 0 at inline-start; move track right (+X) per step. */
  const translateX = isRtl
    ? -padLeft + currentIndex * slideStep
    : -(padLeft + currentIndex * slideStep);

  const trackStyle = {
    opacity: 1,
    transform: `translate3d(${translateX}px, 0, 0)`,
    transition: reduceMotion
      ? "none"
      : "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
  };

  const totalSlides = filteredServices.length;
  const isPrevDisabled = currentIndex === 0 || totalSlides <= 1;
  const isNextDisabled =
    currentIndex >= totalSlides - 1 || totalSlides <= 1;

  const SWIPE_MIN_PX = 50;
  const SWIPE_DOMINANCE = 1.25;

  const onPointerDown = useCallback((e) => {
    swipeConsumedRef.current = false;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    pointerStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      id: e.pointerId,
    };
  }, []);

  const onPointerUp = useCallback(
    (e) => {
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (!start || e.pointerId !== start.id) return;

      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (
        Math.abs(dx) < SWIPE_MIN_PX ||
        Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE
      ) {
        return;
      }

      /* Same gesture as typical carousels: finger moves left → next slide (independent of page dir). */
      const towardNext = dx < 0;
      let moved = false;
      if (towardNext) {
        if (!isNextDisabled) {
          handleNextClick();
          moved = true;
        }
      } else if (!isPrevDisabled) {
        handlePrevClick();
        moved = true;
      }
      if (moved) {
        swipeConsumedRef.current = true;
      }
    },
    [handleNextClick, handlePrevClick, isNextDisabled, isPrevDisabled]
  );

  const onPointerCancel = useCallback(() => {
    pointerStartRef.current = null;
  }, []);

  const onViewportClickCapture = useCallback((e) => {
    if (!swipeConsumedRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    swipeConsumedRef.current = false;
  }, []);

  const onViewportKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (isRtl) {
          if (!isNextDisabled) handleNextClick();
        } else if (!isPrevDisabled) {
          handlePrevClick();
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (isRtl) {
          if (!isPrevDisabled) handlePrevClick();
        } else if (!isNextDisabled) {
          handleNextClick();
        }
      } else if (e.key === "Home") {
        e.preventDefault();
        setCurrentIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setCurrentIndex(Math.max(0, totalSlides - 1));
      }
    },
    [
      handleNextClick,
      handlePrevClick,
      isNextDisabled,
      isPrevDisabled,
      isRtl,
      totalSlides,
    ]
  );

  return (
    <div className="services-picker__slider-wrap vh-services-slider">
      <div
        className="services-picker__slider services-picker__slider--new slick-initialized slick-slider vh-services-slider__root"
        role="region"
        aria-roledescription="carousel"
        aria-label={t("slider.carouselAria")}
      >
        <div
          ref={listRef}
          className="slick-list draggable vh-services-slider__viewport"
          tabIndex={0}
          onKeyDown={onViewportKeyDown}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onClickCapture={onViewportClickCapture}
        >
          <div
            ref={trackRef}
            className="slick-track vh-services-slider__track"
            style={trackStyle}
          >
            {filteredServices.map((service, index) => (
              <ServiceCard
                key={service.id}
                {...service}
                isActive={index === currentIndex}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
      <SliderControls
        onPrevClick={handlePrevClick}
        onNextClick={handleNextClick}
        onResetFilter={handleResetFilter}
        selectedTags={selectedTags}
        isPrevDisabled={isPrevDisabled}
        isNextDisabled={isNextDisabled}
        currentIndex={currentIndex}
        totalSlides={totalSlides}
      />
    </div>
  );
});

ServicesSlider.displayName = "ServicesSlider";

export default ServicesSlider;
